import { action, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
} from '@/features/legends/editor/analyzer-errors'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { QuestionsBuilderViewModel } from '@/features/legends/questions/QuestionsBuilderViewModel'
import type { PathQuestion } from '@/features/legends/questions/questions'
import { validateObject } from '@/utils/validx-util'

/**
 * Groups elements behind shared conditions.
 */
export class PathQuestionEditorViewModel extends QuestionEditorViewModelBase<PathQuestion> {
  private readonly _schema: IValidationSchema<PathQuestionEditorViewModel> = {
    ...QuestionEditorViewModelBase.sharedSchema,
    questions: [validateObject('There are invalid questions.')],
  }

  readonly type = 'Path'

  /**
   * The elements in the path.
   */
  readonly questions: QuestionsBuilderViewModel

  @observable accessor label = ''

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options)
    this.questions = options.parentQuestionsBuilder.createChildBuilder(this)
    this._validateWhileEditing(this._schema)
  }

  /** @inheritDoc */
  get displayText() {
    return this.label
  }

  /** @inheritDoc */
  getAllSubQuestions() {
    return this.questions.flattenedQuestions
  }

  /**
   * Adds an element for every allowed service item the legend doesn't have.
   */
  @action.bound
  addInventoryItems() {
    for (const item of this.dependencies.catalog.allowedInventoryItems) {
      if (!item.id) {
        continue
      }
      const found = this.questions.allQuestions.some(
        (q) => q.type === 'InventoryItem' && q.inventoryItemId === item.id,
      )
      if (!found) {
        const element = this.questions.add('InventoryItem')
        if (element.type === 'InventoryItem') {
          element.changeInventoryItem(item.id)
        }
      }
    }
  }

  /** @inheritDoc */
  @action
  hydrate(question: PathQuestion) {
    this._hydrateShared(question)
    this.label = question.label ?? ''
    if (question.questions) {
      this.questions.hydrate(question.questions)
    }
  }

  /** @inheritDoc */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor.inner?.questions,
      'questions',
      'There are invalid questions',
    )
    super.receiveErrors(descriptor)
  }

  /** @inheritDoc */
  protected _serialize(): PathQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      questions: this.questions.toJSON(),
      label: this.label,
    }
  }
}
